import React from 'react'
import { useIntl } from 'react-intl'

import Room from '../Room'
import { MAX_ROOMS } from '../constants'
import {
  LinkButton,
  PaxSelectorContent,
  PaxSelectorWrapper,
  PaxesFooter,
  PaxesMobileHeader,
} from './styles'
import { Room as RoomType, SearchMode } from 'src/types/search'
import PrimaryButton from 'src/comps/Atom/Buttons/PrimaryButton'
import Close from 'client/icons/Close'

interface RoomWithRoomNumber extends RoomType {
  roomNumber: number
}

const removeRoomNumber = (rooms: RoomWithRoomNumber[]) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return rooms.map(({ roomNumber, ...room }) => room)
}

interface IPaxesSelectorProps {
  readonly rooms: RoomType[]
  readonly isValidationOn: boolean
  readonly onChange: (value: RoomType[]) => void
  readonly onClose: () => void
  readonly handleSave: () => void
  readonly mode: SearchMode
}

function PaxesSelector({
  rooms,
  isValidationOn,
  onChange,
  onClose,
  handleSave,
  mode,
}: IPaxesSelectorProps) {
  const intl = useIntl()
  const roomsWithRoomNumber = rooms.map((room, index) => {
    return {
      roomNumber: index + 1,
      ...room,
    }
  })

  const handleChangeRoom = (roomNumber: number, values: RoomType) => {
    const newRooms = roomsWithRoomNumber.map(room => {
      if (room.roomNumber === roomNumber) {
        return {
          roomNumber,
          ...values,
        }
      }
      return room
    })
    onChange(removeRoomNumber(newRooms))
  }

  const handleRemoveRoom = (roomIndex: number) => {
    const newRooms = rooms
      .filter((_, index) => index !== roomIndex)
      .map((room, index) => ({ ...room, roomNumber: index + 1 }))

    onChange(removeRoomNumber(newRooms))
  }

  const handleAddRoom = (event: React.MouseEvent) => {
    event.preventDefault()

    const newRooms = [
      ...roomsWithRoomNumber,
      {
        roomNumber: rooms.length + 1,
        adults: 2,
        children: [],
      },
    ]
    onChange(removeRoomNumber(newRooms))
  }

  return (
    <PaxSelectorWrapper data-testid="pax-selector" mode={mode}>
      <PaxSelectorContent mode={mode}>
        <PaxesMobileHeader mode={mode}>
          {intl.formatMessage({ id: 'global.guests' })}
          <Close onClick={onClose} size={24} />
        </PaxesMobileHeader>

        {roomsWithRoomNumber.map((room, index) => {
          const position = index
          const roomNumber = position + 1
          const roomKey = `${JSON.stringify(room)}-${position}`
          return (
            <Room
              {...room}
              key={roomKey}
              isValidationOn={isValidationOn}
              onChange={values => handleChangeRoom(roomNumber, values)}
              onRemove={() => handleRemoveRoom(position)}
            />
          )
        })}
      </PaxSelectorContent>
      <PaxesFooter>
        {rooms.length < MAX_ROOMS ? (
          <LinkButton onClick={handleAddRoom}>
            {intl.formatMessage({ id: 'pax.add-room' })}
          </LinkButton>
        ) : (
          <span />
        )}
        <PrimaryButton size="m" type="button" onClick={handleSave}>
          {intl.formatMessage({ id: 'global.save' })}
        </PrimaryButton>
      </PaxesFooter>
    </PaxSelectorWrapper>
  )
}

export default PaxesSelector
